import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  FolderOpen, Plus, Trash2, Send, Lightbulb, MessageSquare, HelpCircle, Ban,
  BookOpen, Zap, Users, TrendingUp, Target, CheckCircle, ArrowUpRight,
  Layers, Shield, Brain, Hash, Heart, Star, AlertTriangle, BarChart3,
  Calendar, Clock, Repeat, FileText, Eye, Search, ChevronRight,
  Activity, Gauge, GraduationCap, Loader2, MessagesSquare
} from 'lucide-react';
import { format } from 'date-fns';
import WorkspaceChat from '@/components/WorkspaceChat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAPTURE_TYPES = [
  { value: 'insight', label: 'Insight', icon: Lightbulb, color: '#FF9500' },
  { value: 'decision', label: 'Decision', icon: Target, color: '#007AFF' },
  { value: 'blocker', label: 'Blocker', icon: Ban, color: '#FF3B30' },
  { value: 'learning', label: 'Learning', icon: BookOpen, color: '#34C759' },
  { value: 'question', label: 'Question', icon: HelpCircle, color: '#AF52DE' },
];

const REACTION_MAP = {
  helpful: { icon: Heart, label: 'Helpful', color: '#FF3B30' },
  reuse: { icon: Repeat, label: 'Reuse', color: '#007AFF' },
  important: { icon: Star, label: 'Important', color: '#FF9500' },
};

const WorkspacePage = () => {
  const { token, user } = useAuth();
  const [captures, setCaptures] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [standups, setStandups] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState('');
  const [captureFilter, setCaptureFilter] = useState('');
  const [newCapture, setNewCapture] = useState({ content: '', capture_type: 'insight', tags: '', room_id: '' });
  const [isRoomDialogOpen, setIsRoomDialogOpen] = useState(false);
  const [roomForm, setRoomForm] = useState({ name: '', description: '', room_type: 'general' });
  const [standupForm, setStandupForm] = useState({ yesterday: '', today: '', blockers: '', knowledge_shared: '' });
  const [standupSubmitted, setStandupSubmitted] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [lessonFilter, setLessonFilter] = useState({ category: '', impact: '', status: '' });
  const [isLessonDialogOpen, setIsLessonDialogOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [lessonForm, setLessonForm] = useState({ title: '', context: '', what_happened: '', root_cause: '', lesson_learned: '', recommended_action: '', category: 'process', impact: 'medium', outcome: 'negative', tags: '' });
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [acceptedFixes, setAcceptedFixes] = useState({});
  const [acceptingFix, setAcceptingFix] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchCaptures(); }, [selectedRoom, captureFilter]);

  const fetchAll = async () => {
    try {
      const [roomsRes, metricsRes, standupsRes] = await Promise.all([
        axios.get(`${API}/workspace/rooms`, { headers }),
        axios.get(`${API}/workspace/metrics`, { headers }),
        axios.get(`${API}/workspace/standups`, { headers }),
      ]);
      setRooms(roomsRes.data);
      setMetrics(metricsRes.data);
      setStandups(standupsRes.data);
      const myStandup = standupsRes.data.find(s => s.user_id === user?.user_id);
      if (myStandup) {
        setStandupForm({ yesterday: myStandup.yesterday, today: myStandup.today, blockers: myStandup.blockers, knowledge_shared: myStandup.knowledge_shared });
        setStandupSubmitted(true);
      }
    } catch {} finally { setLoading(false); }
    fetchCaptures();
  };

  const fetchCaptures = async () => {
    try {
      const params = {};
      if (selectedRoom) params.room_id = selectedRoom;
      if (captureFilter) params.capture_type = captureFilter;
      const res = await axios.get(`${API}/workspace/captures`, { params, headers });
      setCaptures(res.data);
    } catch {}
  };

  const handleCapture = async () => {
    if (!newCapture.content.trim()) { toast.error('Content is required'); return; }
    try {
      const tags = newCapture.tags ? newCapture.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      await axios.post(`${API}/workspace/captures`, { ...newCapture, tags, room_id: selectedRoom }, { headers });
      toast.success('Knowledge captured!');
      setNewCapture({ content: '', capture_type: 'insight', tags: '', room_id: '' });
      fetchCaptures();
      fetchMetrics();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const fetchMetrics = async () => {
    try { const res = await axios.get(`${API}/workspace/metrics`, { headers }); setMetrics(res.data); } catch {}
  };

  const handleReact = async (captureId, reaction) => {
    try {
      await axios.post(`${API}/workspace/captures/${captureId}/react`, { reaction }, { headers });
      fetchCaptures();
    } catch {}
  };

  const handlePromote = async (captureId) => {
    try {
      await axios.post(`${API}/workspace/captures/${captureId}/promote`, {}, { headers });
      toast.success('Promoted to knowledge base article!');
      fetchCaptures(); fetchMetrics();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteCapture = async (captureId) => {
    try { await axios.delete(`${API}/workspace/captures/${captureId}`, { headers }); fetchCaptures(); fetchMetrics(); } catch {}
  };

  const handleCreateRoom = async () => {
    if (!roomForm.name.trim()) { toast.error('Room name required'); return; }
    try {
      await axios.post(`${API}/workspace/rooms`, roomForm, { headers });
      toast.success('Room created'); setIsRoomDialogOpen(false); setRoomForm({ name: '', description: '', room_type: 'general' });
      const res = await axios.get(`${API}/workspace/rooms`, { headers }); setRooms(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteRoom = async (roomId) => {
    try { await axios.delete(`${API}/workspace/rooms/${roomId}`, { headers }); const res = await axios.get(`${API}/workspace/rooms`, { headers }); setRooms(res.data); if (selectedRoom === roomId) setSelectedRoom(''); } catch {}
  };

  const handleStandup = async () => {
    try {
      await axios.post(`${API}/workspace/standup`, standupForm, { headers });
      toast.success('Standup posted!'); setStandupSubmitted(true);
      const res = await axios.get(`${API}/workspace/standups`, { headers }); setStandups(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const getCaptureType = (type) => CAPTURE_TYPES.find(t => t.value === type) || CAPTURE_TYPES[0];

  const fetchLessons = async () => {
    try {
      const params = {};
      if (lessonFilter.category) params.category = lessonFilter.category;
      if (lessonFilter.impact) params.impact = lessonFilter.impact;
      if (lessonFilter.status) params.status = lessonFilter.status;
      const res = await axios.get(`${API}/workspace/lessons`, { params, headers });
      setLessons(res.data);
    } catch {}
    fetchAnalysis();
  };

  const handleCreateLesson = async () => {
    if (!lessonForm.title.trim()) { toast.error('Title is required'); return; }
    try {
      const tags = lessonForm.tags ? lessonForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      const payload = { ...lessonForm, tags };
      if (editingLesson) {
        await axios.put(`${API}/workspace/lessons/${editingLesson}`, payload, { headers });
        toast.success('Lesson updated');
      } else {
        await axios.post(`${API}/workspace/lessons`, payload, { headers });
        toast.success('Lesson learned captured!');
      }
      setIsLessonDialogOpen(false);
      setEditingLesson(null);
      setLessonForm({ title: '', context: '', what_happened: '', root_cause: '', lesson_learned: '', recommended_action: '', category: 'process', impact: 'medium', outcome: 'negative', tags: '' });
      fetchLessons(); fetchMetrics();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleLessonStatus = async (lessonId, newStatus, actionTaken) => {
    try {
      const payload = { status: newStatus };
      if (actionTaken) payload.action_taken = actionTaken;
      await axios.put(`${API}/workspace/lessons/${lessonId}`, payload, { headers });
      toast.success('Status updated'); fetchLessons(); fetchMetrics();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleVoteLesson = async (lessonId) => {
    try { await axios.post(`${API}/workspace/lessons/${lessonId}/vote`, {}, { headers }); fetchLessons(); }
    catch {}
  };

  const handleDeleteLesson = async (lessonId) => {
    try { await axios.delete(`${API}/workspace/lessons/${lessonId}`, { headers }); fetchLessons(); fetchMetrics(); } catch {}
  };

  const openEditLesson = (lesson) => {
    setEditingLesson(lesson.lesson_id);
    setLessonForm({
      title: lesson.title, context: lesson.context || '', what_happened: lesson.what_happened || '',
      root_cause: lesson.root_cause || '', lesson_learned: lesson.lesson_learned || '',
      recommended_action: lesson.recommended_action || '', category: lesson.category || 'process',
      impact: lesson.impact || 'medium', outcome: lesson.outcome || 'negative',
      tags: (lesson.tags || []).join(', '),
    });
    setIsLessonDialogOpen(true);
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/workspace/lessons/analyze`, {}, { headers });
      setAnalysis(res.data);
      toast.success('Pattern analysis complete!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Analysis failed — need at least 2 lessons'); }
    finally { setAnalyzing(false); }
  };

  const fetchAnalysis = async () => {
    try {
      const res = await axios.get(`${API}/workspace/lessons/analysis`, { headers });
      if (res.data) setAnalysis(res.data);
    } catch {}
  };

  const handleAcceptFix = async (fix, index) => {
    setAcceptingFix(index);
    try {
      await axios.post(`${API}/workspace/lessons/accept-fix`, {
        fix: fix.fix, priority: fix.priority, effort: fix.effort,
        expected_impact: fix.expected_impact, addresses_theme: fix.addresses_theme,
      }, { headers });
      setAcceptedFixes(prev => ({ ...prev, [index]: true }));
      toast.success(`Process improvement task created!`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setAcceptingFix(null); }
  };

  const [convertingLesson, setConvertingLesson] = useState(null);
  const [bulkConverting, setBulkConverting] = useState(false);

  const handleConvertToTask = async (lesson) => {
    setConvertingLesson(lesson.lesson_id);
    try {
      const res = await axios.post(`${API}/workspace/lessons/${lesson.lesson_id}/convert-to-task`, {}, { headers });
      toast.success(res.data.message);
      fetchLessons();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to convert'); }
    finally { setConvertingLesson(null); }
  };

  const handleBulkConvert = async () => {
    setBulkConverting(true);
    try {
      const res = await axios.post(`${API}/workspace/lessons/bulk-convert`, {}, { headers });
      toast.success(res.data.message);
      fetchLessons();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBulkConverting(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div data-testid="workspace-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Collaborative Workspace</h1>
          <p className="text-[#4B5563] text-sm">KCS-powered knowledge management for scalable remote teams</p>
        </div>
        <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => setIsRoomDialogOpen(true)} data-testid="create-room-btn">
          <Plus className="w-4 h-4 mr-2" />New Room
        </Button>
      </div>

      <Tabs defaultValue="workspace" onValueChange={(v) => { if (v === 'metrics') fetchMetrics(); if (v === 'lessons') fetchLessons(); }}>
        <TabsList className="w-full justify-start mb-4 flex-wrap">
          <TabsTrigger value="workspace" data-testid="tab-workspace"><Zap className="w-3.5 h-3.5 mr-1" />Live Workspace</TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat"><MessagesSquare className="w-3.5 h-3.5 mr-1" />Live Chat</TabsTrigger>
          <TabsTrigger value="lessons" data-testid="tab-lessons"><GraduationCap className="w-3.5 h-3.5 mr-1" />Lessons Learned</TabsTrigger>
          <TabsTrigger value="standup" data-testid="tab-standup"><Calendar className="w-3.5 h-3.5 mr-1" />Daily Standup</TabsTrigger>
          <TabsTrigger value="methodology" data-testid="tab-methodology"><Layers className="w-3.5 h-3.5 mr-1" />KCS Methodology</TabsTrigger>
          <TabsTrigger value="culture" data-testid="tab-culture"><Users className="w-3.5 h-3.5 mr-1" />Culture Practices</TabsTrigger>
          <TabsTrigger value="metrics" data-testid="tab-metrics"><BarChart3 className="w-3.5 h-3.5 mr-1" />Success Metrics</TabsTrigger>
        </TabsList>

        <TabsContent value="chat">
          <WorkspaceChat rooms={rooms} />
        </TabsContent>

        {/* ==================== LIVE WORKSPACE ==================== */}
        <TabsContent value="workspace" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            {/* Rooms Sidebar */}
            <div className="space-y-2" data-testid="rooms-panel">
              <h3 className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider">Rooms</h3>
              <button onClick={() => setSelectedRoom('')}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors ${!selectedRoom ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'hover:bg-[#F3F4F6] text-[#4B5563]'}`}
                data-testid="room-all">
                <Hash className="w-3 h-3 inline mr-1.5" />All Captures
              </button>
              {rooms.map(r => (
                <div key={r.room_id} className="flex items-center gap-1">
                  <button onClick={() => setSelectedRoom(r.room_id)}
                    className={`flex-1 text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors ${selectedRoom === r.room_id ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'hover:bg-[#F3F4F6] text-[#4B5563]'}`}
                    data-testid={`room-${r.room_id}`}>
                    <Hash className="w-3 h-3 inline mr-1.5" />{r.name}
                  </button>
                  <Button variant="ghost" size="icon" className="h-6 w-6 text-[#FF3B30] opacity-0 hover:opacity-100" onClick={() => handleDeleteRoom(r.room_id)}><Trash2 className="w-2.5 h-2.5" /></Button>
                </div>
              ))}
            </div>

            {/* Main Feed */}
            <div className="lg:col-span-3 space-y-4">
              {/* Quick Capture */}
              <Card className="border-[#002FA7]/20 bg-[#002FA7]/[0.02]" data-testid="capture-form">
                <CardContent className="p-4">
                  <div className="flex gap-2 mb-2">
                    <Select value={newCapture.capture_type} onValueChange={(v) => setNewCapture(p => ({ ...p, capture_type: v }))}>
                      <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="capture-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CAPTURE_TYPES.map(t => {
                          const Icon = t.icon;
                          return <SelectItem key={t.value} value={t.value}><span className="flex items-center gap-1.5"><Icon className="w-3 h-3" style={{ color: t.color }} />{t.label}</span></SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                    <Input placeholder="Tags (comma-separated)" value={newCapture.tags} onChange={e => setNewCapture(p => ({ ...p, tags: e.target.value }))} className="h-8 text-xs flex-1 max-w-[200px]" data-testid="capture-tags" />
                  </div>
                  <div className="flex gap-2">
                    <Textarea placeholder="Capture knowledge as it happens — insights, decisions, blockers, learnings..." value={newCapture.content} onChange={e => setNewCapture(p => ({ ...p, content: e.target.value }))} rows={2} className="text-xs resize-none" data-testid="capture-input" />
                    <Button className="bg-[#002FA7] hover:bg-[#00227A] h-auto self-end" onClick={handleCapture} data-testid="capture-submit"><Send className="w-4 h-4" /></Button>
                  </div>
                </CardContent>
              </Card>

              {/* Filter */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-[#9CA3AF]">Filter:</span>
                <button onClick={() => setCaptureFilter('')} className={`px-2 py-1 rounded text-[10px] font-medium ${!captureFilter ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'text-[#9CA3AF] hover:bg-[#F3F4F6]'}`}>All</button>
                {CAPTURE_TYPES.map(t => (
                  <button key={t.value} onClick={() => setCaptureFilter(t.value)}
                    className={`px-2 py-1 rounded text-[10px] font-medium ${captureFilter === t.value ? 'text-white' : 'hover:bg-[#F3F4F6]'}`}
                    style={captureFilter === t.value ? { backgroundColor: t.color } : { color: t.color }}
                    data-testid={`filter-${t.value}`}>
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Captures Feed */}
              <div className="space-y-2" data-testid="captures-feed">
                {captures.length === 0 ? (
                  <Card className="border-[#E5E7EB]"><CardContent className="p-8 text-center text-[#9CA3AF]">
                    <Lightbulb className="w-8 h-8 mx-auto mb-2 text-[#E5E7EB]" />
                    <p className="text-sm">No knowledge captures yet. Start by sharing an insight!</p>
                  </CardContent></Card>
                ) : captures.map(cap => {
                  const ct = getCaptureType(cap.capture_type);
                  const Icon = ct.icon;
                  const reactionCount = Object.keys(cap.reactions || {}).length;
                  return (
                    <Card key={cap.capture_id} className="border-[#E5E7EB] hover:border-[#002FA7]/20 transition-colors" data-testid={`capture-${cap.capture_id}`}>
                      <CardContent className="p-3">
                        <div className="flex gap-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: ct.color + '15' }}>
                            <Icon className="w-4 h-4" style={{ color: ct.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[10px] font-semibold text-[#0A0A0A]">{cap.user_name}</span>
                              <Badge className="text-[8px] px-1.5 py-0" style={{ backgroundColor: ct.color + '15', color: ct.color }}>{ct.label}</Badge>
                              <span className="text-[9px] text-[#9CA3AF]">{format(new Date(cap.created_at), 'MMM d, h:mm a')}</span>
                              {cap.promoted_to_article && <Badge className="text-[8px] bg-[#34C759]/10 text-[#34C759] px-1 py-0">In KB</Badge>}
                            </div>
                            <p className="text-xs text-[#4B5563] leading-relaxed">{cap.content}</p>
                            {cap.tags?.length > 0 && (
                              <div className="flex gap-1 mt-1.5 flex-wrap">
                                {cap.tags.map((tag, i) => <Badge key={i} variant="outline" className="text-[8px] px-1 border-[#E5E7EB]">{tag}</Badge>)}
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              {Object.entries(REACTION_MAP).map(([key, r]) => {
                                const RIcon = r.icon;
                                const myReaction = cap.reactions?.[user?.user_id] === key;
                                return (
                                  <button key={key} onClick={() => handleReact(cap.capture_id, key)}
                                    className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] transition-colors ${myReaction ? 'bg-opacity-20 font-medium' : 'hover:bg-[#F3F4F6]'}`}
                                    style={myReaction ? { backgroundColor: r.color + '20', color: r.color } : { color: '#9CA3AF' }}
                                    data-testid={`react-${key}-${cap.capture_id}`}>
                                    <RIcon className="w-2.5 h-2.5" />{r.label}
                                  </button>
                                );
                              })}
                              {!cap.promoted_to_article && (
                                <button onClick={() => handlePromote(cap.capture_id)} className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] text-[#34C759] hover:bg-[#34C759]/10" data-testid={`promote-${cap.capture_id}`}>
                                  <ArrowUpRight className="w-2.5 h-2.5" />Promote to KB
                                </button>
                              )}
                              <button onClick={() => handleDeleteCapture(cap.capture_id)} className="ml-auto text-[9px] text-[#FF3B30] hover:bg-[#FF3B30]/10 px-1.5 py-0.5 rounded">
                                <Trash2 className="w-2.5 h-2.5" />
                              </button>
                              {reactionCount > 0 && <span className="text-[8px] text-[#9CA3AF] ml-auto">{reactionCount} reaction{reactionCount > 1 ? 's' : ''}</span>}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </TabsContent>


        {/* ==================== LESSONS LEARNED ==================== */}
        <TabsContent value="lessons" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Select value={lessonFilter.category || "all"} onValueChange={v => { setLessonFilter(p => ({ ...p, category: v === 'all' ? '' : v })); setTimeout(fetchLessons, 0); }}>
                <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="lesson-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="process">Process</SelectItem>
                  <SelectItem value="technical">Technical</SelectItem>
                  <SelectItem value="communication">Communication</SelectItem>
                  <SelectItem value="planning">Planning</SelectItem>
                  <SelectItem value="risk">Risk</SelectItem>
                </SelectContent>
              </Select>
              <Select value={lessonFilter.impact || "all"} onValueChange={v => { setLessonFilter(p => ({ ...p, impact: v === 'all' ? '' : v })); setTimeout(fetchLessons, 0); }}>
                <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="lesson-impact-filter"><SelectValue placeholder="Impact" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Impact</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={lessonFilter.status || "all"} onValueChange={v => { setLessonFilter(p => ({ ...p, status: v === 'all' ? '' : v })); setTimeout(fetchLessons, 0); }}>
                <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="lesson-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="action-taken">Action Taken</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="h-8 text-xs border-[#34C759]/30 text-[#34C759]" onClick={handleBulkConvert} disabled={bulkConverting || lessons.filter(l => l.status === 'open' && l.recommended_action && !l.linked_task_id).length === 0} data-testid="bulk-convert-btn">
                {bulkConverting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Converting...</> : <><Zap className="w-3 h-3 mr-1" />Bulk Convert to Tasks ({lessons.filter(l => l.status === 'open' && l.recommended_action && !l.linked_task_id).length})</>}
              </Button>
              <Button variant="outline" className="h-8 text-xs border-[#5856D6]/30 text-[#5856D6]" onClick={handleAnalyze} disabled={analyzing || lessons.length < 2} data-testid="analyze-btn">
                {analyzing ? <><Activity className="w-3 h-3 mr-1 animate-spin" />Analyzing...</> : <><Brain className="w-3 h-3 mr-1" />Detect Patterns</>}
              </Button>
              <Button className="bg-[#002FA7] hover:bg-[#00227A] h-8 text-xs" onClick={() => { setEditingLesson(null); setLessonForm({ title: '', context: '', what_happened: '', root_cause: '', lesson_learned: '', recommended_action: '', category: 'process', impact: 'medium', outcome: 'negative', tags: '' }); setIsLessonDialogOpen(true); }} data-testid="create-lesson-btn">
                <Plus className="w-3 h-3 mr-1" />Capture Lesson
              </Button>
            </div>
          </div>

          {/* AI Pattern Analysis Results */}
          {analysis?.result && (
            <Card className="border-[#5856D6]/20 bg-[#5856D6]/[0.02]" data-testid="pattern-analysis">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2"><Brain className="w-4 h-4 text-[#5856D6]" />AI Pattern Analysis</CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge className="text-[9px]" style={{
                      backgroundColor: (analysis.result.health_score || 0) >= 70 ? '#34C75915' : (analysis.result.health_score || 0) >= 40 ? '#FF950015' : '#FF3B3015',
                      color: (analysis.result.health_score || 0) >= 70 ? '#34C759' : (analysis.result.health_score || 0) >= 40 ? '#FF9500' : '#FF3B30',
                    }} data-testid="health-score-badge">
                      Learning Health: {analysis.result.health_score || 0}/100
                    </Badge>
                    <span className="text-[9px] text-[#9CA3AF]">{analysis.lessons_analyzed} lessons analyzed</span>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Executive Summary */}
                <p className="text-xs text-[#4B5563] leading-relaxed bg-white p-3 rounded-lg border border-[#E5E7EB]" data-testid="analysis-summary">
                  {analysis.result.executive_summary}
                </p>
                <p className="text-[10px] text-[#5856D6] italic">{analysis.result.health_assessment}</p>

                {/* Recurring Themes */}
                {analysis.result.recurring_themes?.length > 0 && (
                  <div data-testid="recurring-themes">
                    <h4 className="text-xs font-semibold text-[#0A0A0A] mb-2 flex items-center gap-1.5"><Repeat className="w-3.5 h-3.5 text-[#FF9500]" />Recurring Themes</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {analysis.result.recurring_themes.map((theme, i) => {
                        const sevColor = { high: '#FF3B30', medium: '#FF9500', low: '#34C759', critical: '#FF3B30' }[theme.severity] || '#FF9500';
                        return (
                          <div key={i} className="p-3 rounded-lg border border-[#E5E7EB] bg-white" data-testid={`theme-${i}`}>
                            <div className="flex items-center justify-between mb-1">
                              <h5 className="text-xs font-semibold text-[#0A0A0A]">{theme.theme}</h5>
                              <div className="flex gap-1">
                                <Badge className="text-[8px] bg-[#5856D6]/10 text-[#5856D6] px-1.5">{theme.frequency}x</Badge>
                                <Badge className="text-[8px] px-1.5" style={{ backgroundColor: sevColor + '15', color: sevColor }}>{theme.severity}</Badge>
                              </div>
                            </div>
                            <p className="text-[10px] text-[#4B5563] mb-1.5">{theme.description}</p>
                            {theme.related_lessons?.length > 0 && (
                              <div className="flex gap-1 flex-wrap">
                                {theme.related_lessons.map((l, j) => <Badge key={j} variant="outline" className="text-[7px] border-[#E5E7EB]">{l.length > 30 ? l.substring(0, 30) + '...' : l}</Badge>)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Systemic Fixes */}
                  {analysis.result.systemic_fixes?.length > 0 && (
                    <div data-testid="systemic-fixes">
                      <h4 className="text-xs font-semibold text-[#0A0A0A] mb-2 flex items-center gap-1.5"><Lightbulb className="w-3.5 h-3.5 text-[#34C759]" />Systemic Fixes</h4>
                      <div className="space-y-2">
                        {analysis.result.systemic_fixes.map((fix, i) => {
                          const priColor = { critical: '#FF3B30', high: '#FF9500', medium: '#007AFF', low: '#34C759' }[fix.priority] || '#007AFF';
                          const effColor = { low: '#34C759', medium: '#FF9500', high: '#FF3B30' }[fix.effort] || '#FF9500';
                          return (
                            <div key={i} className="p-2.5 rounded-lg border-l-[3px] border border-[#E5E7EB] bg-white" style={{ borderLeftColor: priColor }} data-testid={`fix-${i}`}>
                              <div className="flex items-center justify-between mb-0.5">
                                <h5 className="text-[11px] font-semibold text-[#0A0A0A] flex-1 pr-2">{fix.fix}</h5>
                                <div className="flex gap-1 flex-shrink-0">
                                  <Badge className="text-[7px] px-1" style={{ backgroundColor: priColor + '15', color: priColor }}>{fix.priority}</Badge>
                                  <Badge className="text-[7px] px-1" style={{ backgroundColor: effColor + '15', color: effColor }}>{fix.effort} effort</Badge>
                                </div>
                              </div>
                              <p className="text-[10px] text-[#4B5563]">{fix.expected_impact}</p>
                              <div className="flex items-center justify-between mt-1">
                                <p className="text-[9px] text-[#9CA3AF]">Addresses: {fix.addresses_theme}</p>
                                {acceptedFixes[i] ? (
                                  <span className="text-[9px] font-medium text-[#34C759] flex items-center gap-0.5" data-testid={`fix-accepted-${i}`}><CheckCircle className="w-3 h-3" />Task Created</span>
                                ) : (
                                  <Button size="sm" variant="outline" className="h-5 text-[8px] px-1.5 border-[#002FA7]/30 text-[#002FA7]"
                                    onClick={() => handleAcceptFix(fix, i)} disabled={acceptingFix === i} data-testid={`accept-fix-${i}`}>
                                    {acceptingFix === i ? <Loader2 className="w-2 h-2 animate-spin mr-0.5" /> : <Zap className="w-2 h-2 mr-0.5" />}
                                    Create Task
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Risk Predictions */}
                  {analysis.result.risk_predictions?.length > 0 && (
                    <div data-testid="risk-predictions">
                      <h4 className="text-xs font-semibold text-[#0A0A0A] mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-[#FF3B30]" />Risk Predictions</h4>
                      <div className="space-y-2">
                        {analysis.result.risk_predictions.map((risk, i) => {
                          const probColor = { high: '#FF3B30', medium: '#FF9500', low: '#34C759' }[risk.probability] || '#FF9500';
                          return (
                            <div key={i} className="p-2.5 rounded-lg border border-[#FF3B30]/20 bg-[#FF3B30]/[0.02]" data-testid={`risk-${i}`}>
                              <div className="flex items-center justify-between mb-1">
                                <h5 className="text-[11px] font-semibold text-[#0A0A0A]">{risk.risk}</h5>
                                <Badge className="text-[7px] px-1.5" style={{ backgroundColor: probColor + '15', color: probColor }}>{risk.probability} prob.</Badge>
                              </div>
                              <p className="text-[10px] text-[#4B5563] mb-1">{risk.based_on}</p>
                              <div className="p-1.5 rounded bg-[#34C759]/5 border border-[#34C759]/20">
                                <p className="text-[9px] text-[#34C759]"><strong>Mitigation:</strong> {risk.mitigation}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {lessons.length === 0 ? (
            <Card className="border-[#E5E7EB]"><CardContent className="p-8 text-center text-[#9CA3AF]">
              <GraduationCap className="w-8 h-8 mx-auto mb-2 text-[#E5E7EB]" />
              <p className="text-sm">No lessons learned captured yet. Click "Capture Lesson" to record what went well or what to improve.</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {lessons.map(les => {
                const impactColor = { critical: '#FF3B30', high: '#FF9500', medium: '#007AFF', low: '#34C759' }[les.impact] || '#007AFF';
                const outcomeColor = { positive: '#34C759', negative: '#FF3B30', neutral: '#9CA3AF' }[les.outcome] || '#9CA3AF';
                const statusColor = { open: '#FF9500', 'action-taken': '#007AFF', closed: '#34C759' }[les.status] || '#9CA3AF';
                const voteCount = les.votes?.length || 0;
                const hasVoted = les.votes?.includes(user?.user_id);
                return (
                  <Card key={les.lesson_id} className="border-[#E5E7EB]" data-testid={`lesson-${les.lesson_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="text-sm font-semibold text-[#0A0A0A]">{les.title}</h3>
                            <Badge className="text-[8px] px-1.5 py-0" style={{ backgroundColor: impactColor + '15', color: impactColor }}>{les.impact}</Badge>
                            <Badge className="text-[8px] px-1.5 py-0" style={{ backgroundColor: outcomeColor + '15', color: outcomeColor }}>{les.outcome}</Badge>
                            <Badge className="text-[8px] px-1.5 py-0" style={{ backgroundColor: statusColor + '15', color: statusColor }}>{les.status}</Badge>
                            <Badge variant="outline" className="text-[8px] px-1.5 py-0 border-[#E5E7EB]">{les.category}</Badge>
                          </div>
                          <p className="text-[10px] text-[#9CA3AF]">{les.user_name} · {format(new Date(les.created_at), 'MMM d, yyyy')}</p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button onClick={() => handleVoteLesson(les.lesson_id)}
                            className={`flex items-center gap-0.5 px-2 py-1 rounded text-[10px] transition-colors ${hasVoted ? 'bg-[#002FA7]/10 text-[#002FA7] font-medium' : 'text-[#9CA3AF] hover:bg-[#F3F4F6]'}`}
                            data-testid={`vote-${les.lesson_id}`}>
                            <Star className={`w-3 h-3 ${hasVoted ? 'fill-[#002FA7]' : ''}`} />{voteCount}
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                        {les.context && <div className="p-2 rounded bg-[#F9FAFB]"><p className="text-[9px] text-[#9CA3AF] font-medium mb-0.5">Context</p><p className="text-[11px] text-[#4B5563]">{les.context}</p></div>}
                        {les.what_happened && <div className="p-2 rounded bg-[#F9FAFB]"><p className="text-[9px] text-[#9CA3AF] font-medium mb-0.5">What Happened</p><p className="text-[11px] text-[#4B5563]">{les.what_happened}</p></div>}
                        {les.root_cause && <div className="p-2 rounded bg-[#FF3B30]/[0.03] border border-[#FF3B30]/10"><p className="text-[9px] text-[#FF3B30] font-medium mb-0.5">Root Cause</p><p className="text-[11px] text-[#4B5563]">{les.root_cause}</p></div>}
                        {les.lesson_learned && <div className="p-2 rounded bg-[#FF9500]/[0.03] border border-[#FF9500]/10"><p className="text-[9px] text-[#FF9500] font-medium mb-0.5">Lesson Learned</p><p className="text-[11px] text-[#4B5563]">{les.lesson_learned}</p></div>}
                      </div>

                      {les.recommended_action && (
                        <div className="p-2 rounded bg-[#34C759]/[0.03] border border-[#34C759]/10 mb-2">
                          <p className="text-[9px] text-[#34C759] font-medium mb-0.5">Recommended Action</p>
                          <p className="text-[11px] text-[#4B5563]">{les.recommended_action}</p>
                        </div>
                      )}

                      {les.action_taken && (
                        <div className="p-2 rounded bg-[#007AFF]/[0.03] border border-[#007AFF]/10 mb-2">
                          <p className="text-[9px] text-[#007AFF] font-medium mb-0.5">Action Taken</p>
                          <p className="text-[11px] text-[#4B5563]">{les.action_taken}</p>
                        </div>
                      )}

                      {les.tags?.length > 0 && <div className="flex gap-1 flex-wrap mb-2">{les.tags.map((t, i) => <Badge key={i} variant="outline" className="text-[8px] px-1 border-[#E5E7EB]">{t}</Badge>)}</div>}

                      {/* Linked Task Indicator */}
                      {les.linked_task_id && (
                        <div className="p-1.5 rounded bg-[#002FA7]/5 border border-[#002FA7]/10 mb-2 flex items-center gap-1.5" data-testid={`linked-task-${les.lesson_id}`}>
                          <CheckCircle className="w-3 h-3 text-[#002FA7]" />
                          <span className="text-[10px] text-[#002FA7] font-medium">Linked Task: {les.linked_task_id}</span>
                        </div>
                      )}

                      <div className="flex items-center gap-1 pt-2 border-t border-[#F3F4F6]">
                        {les.status === 'open' && !les.linked_task_id && (
                          <Button size="sm" variant="outline" className="h-6 text-[9px] text-[#34C759] border-[#34C759]/30"
                            onClick={() => handleConvertToTask(les)} disabled={convertingLesson === les.lesson_id}
                            data-testid={`convert-task-${les.lesson_id}`}>
                            {convertingLesson === les.lesson_id ? <Loader2 className="w-2.5 h-2.5 mr-0.5 animate-spin" /> : <Zap className="w-2.5 h-2.5 mr-0.5" />}
                            Convert to Task
                          </Button>
                        )}
                        {les.status === 'open' && (
                          <Button size="sm" variant="outline" className="h-6 text-[9px] text-[#007AFF] border-[#007AFF]/30" onClick={() => { const action = prompt('What action was taken?'); if (action) handleLessonStatus(les.lesson_id, 'action-taken', action); }} data-testid={`action-${les.lesson_id}`}>
                            <CheckCircle className="w-2.5 h-2.5 mr-0.5" />Mark Action Taken
                          </Button>
                        )}
                        {les.status === 'action-taken' && (
                          <Button size="sm" variant="outline" className="h-6 text-[9px] text-[#34C759] border-[#34C759]/30" onClick={() => handleLessonStatus(les.lesson_id, 'closed')} data-testid={`close-${les.lesson_id}`}>
                            <CheckCircle className="w-2.5 h-2.5 mr-0.5" />Close
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-6 text-[9px]" onClick={() => openEditLesson(les)} data-testid={`edit-lesson-${les.lesson_id}`}>Edit</Button>
                        <Button size="sm" variant="ghost" className="h-6 text-[9px] text-[#FF3B30] ml-auto" onClick={() => handleDeleteLesson(les.lesson_id)} data-testid={`delete-lesson-${les.lesson_id}`}><Trash2 className="w-2.5 h-2.5" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ==================== DAILY STANDUP ==================== */}
        <TabsContent value="standup" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* My Standup */}
            <Card className="border-[#E5E7EB]" data-testid="my-standup">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Calendar className="w-4 h-4 text-[#002FA7]" />My Standup — {format(new Date(), 'MMM d, yyyy')}</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div><Label className="text-xs">What I did yesterday</Label><Textarea rows={2} className="text-xs" value={standupForm.yesterday} onChange={e => setStandupForm(p => ({ ...p, yesterday: e.target.value }))} placeholder="Completed tasks, progress made..." data-testid="standup-yesterday" /></div>
                <div><Label className="text-xs">What I'm working on today</Label><Textarea rows={2} className="text-xs" value={standupForm.today} onChange={e => setStandupForm(p => ({ ...p, today: e.target.value }))} placeholder="Planned tasks, priorities..." data-testid="standup-today" /></div>
                <div><Label className="text-xs">Blockers</Label><Textarea rows={1} className="text-xs" value={standupForm.blockers} onChange={e => setStandupForm(p => ({ ...p, blockers: e.target.value }))} placeholder="Any blockers or dependencies?" data-testid="standup-blockers" /></div>
                <div><Label className="text-xs">Knowledge shared / learned</Label><Textarea rows={1} className="text-xs" value={standupForm.knowledge_shared} onChange={e => setStandupForm(p => ({ ...p, knowledge_shared: e.target.value }))} placeholder="Insights, tips, or learnings to share with the team..." data-testid="standup-knowledge" /></div>
                <Button className={`w-full ${standupSubmitted ? 'bg-[#34C759] hover:bg-[#2DB84E]' : 'bg-[#002FA7] hover:bg-[#00227A]'}`} onClick={handleStandup} data-testid="submit-standup">
                  {standupSubmitted ? <><CheckCircle className="w-4 h-4 mr-1.5" />Update Standup</> : <><Send className="w-4 h-4 mr-1.5" />Post Standup</>}
                </Button>
              </CardContent>
            </Card>

            {/* Team Standups */}
            <Card className="border-[#E5E7EB]" data-testid="team-standups">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4 text-[#5856D6]" />Team Standups ({standups.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
                {standups.length === 0 ? (
                  <p className="text-xs text-[#9CA3AF] text-center py-4">No standups posted today yet</p>
                ) : standups.map((s, i) => (
                  <div key={i} className="p-3 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB]" data-testid={`standup-entry-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-[#0A0A0A]">{s.user_name}</span>
                      <span className="text-[9px] text-[#9CA3AF]">{format(new Date(s.created_at), 'h:mm a')}</span>
                    </div>
                    {s.yesterday && <div className="mb-1"><span className="text-[9px] text-[#9CA3AF]">Yesterday:</span><p className="text-[11px] text-[#4B5563]">{s.yesterday}</p></div>}
                    {s.today && <div className="mb-1"><span className="text-[9px] text-[#9CA3AF]">Today:</span><p className="text-[11px] text-[#4B5563]">{s.today}</p></div>}
                    {s.blockers && <div className="mb-1"><span className="text-[9px] text-[#FF3B30]">Blockers:</span><p className="text-[11px] text-[#FF3B30]">{s.blockers}</p></div>}
                    {s.knowledge_shared && <div className="p-1.5 rounded bg-[#34C759]/5 border border-[#34C759]/20"><span className="text-[9px] text-[#34C759]">Knowledge shared:</span><p className="text-[11px] text-[#4B5563]">{s.knowledge_shared}</p></div>}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ==================== KCS METHODOLOGY ==================== */}
        <TabsContent value="methodology" className="space-y-4">
          <Card className="border-[#E5E7EB]" data-testid="methodology-card">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Layers className="w-4 h-4 text-[#002FA7]" />KCS (Knowledge-Centered Service) Daily Workflow</CardTitle></CardHeader>
            <CardContent>
              <p className="text-xs text-[#4B5563] mb-4 bg-[#F9FAFB] p-3 rounded-lg border border-[#E5E7EB]">Knowledge capture happens as a natural byproduct of work — not a separate task. Every interaction creates or improves knowledge. This workflow integrates seamlessly into KStreak's existing tools.</p>
              <div className="space-y-0">
                {[
                  { time: '9:00 AM', title: 'Async Standup', desc: 'Post your standup in the Daily Standup tab — yesterday, today, blockers, and knowledge shared. This replaces sync meetings for distributed teams.', icon: Calendar, color: '#007AFF', tool: 'Workspace > Daily Standup' },
                  { time: '9:15 AM', title: 'Capture While Working', desc: 'As you work on tasks, capture insights, decisions, and learnings in the Live Workspace feed. Tag them by type (Insight/Decision/Blocker/Learning/Question). This is KCS "Capture in the workflow".', icon: Lightbulb, color: '#FF9500', tool: 'Workspace > Live Workspace' },
                  { time: 'Throughout', title: 'Reuse Before Creating', desc: 'Before starting a new task, search the knowledge feed and KB for existing solutions. React with "Reuse" when you find helpful captures. This drives the KCS "Solve" loop.', icon: Search, color: '#5856D6', tool: 'Workspace search + KB articles' },
                  { time: 'Throughout', title: 'Review & Annotate Documents', desc: 'For document review tasks, use the Annotation system to place pins, @mention reviewers, and build threaded discussions. Knowledge is captured at the point of need.', icon: MessageSquare, color: '#AF52DE', tool: 'Tasks > Review & Annotate' },
                  { time: '3:00 PM', title: 'Promote to Knowledge Base', desc: 'Review the day\'s captures. High-value insights get "Promoted to KB" — becoming searchable articles for the whole org. This is the KCS "Evolve" phase.', icon: ArrowUpRight, color: '#34C759', tool: 'Workspace > Promote to KB' },
                  { time: '5:00 PM', title: 'Check Workflow Report', desc: 'Review the Task Workflow Report for blockers, at-risk items, and prescriptive actions. Ensure knowledge gaps are captured before EOD.', icon: Shield, color: '#FF3B30', tool: 'Tasks > Workflow Report' },
                ].map((step, i) => (
                  <div key={i} className="flex gap-4 py-3 border-b border-[#F3F4F6] last:border-0" data-testid={`workflow-step-${i}`}>
                    <div className="text-center flex-shrink-0 w-16">
                      <span className="text-[10px] font-bold" style={{ color: step.color }}>{step.time}</span>
                    </div>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: step.color + '12' }}>
                      <step.icon className="w-4 h-4" style={{ color: step.color }} />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xs font-semibold text-[#0A0A0A] mb-0.5">{step.title}</h4>
                      <p className="text-[11px] text-[#4B5563] leading-relaxed">{step.desc}</p>
                      <Badge variant="outline" className="text-[8px] mt-1 border-[#E5E7EB]">{step.tool}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Tech Stack Integration */}
          <Card className="border-[#E5E7EB]" data-testid="tech-stack-card">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Zap className="w-4 h-4 text-[#FF9500]" />Technology Stack Integration — Zero Context Switching</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { tool: 'Central Knowledge Base', desc: 'KStreak Articles + Workspace Captures with search, tagging, and version history', integration: 'Captures auto-promote to Articles', icon: BookOpen, color: '#002FA7' },
                  { tool: 'Team Communication', desc: 'Workspace rooms + @mentions + notification system + reply threads', integration: 'In-context at every annotation and capture', icon: MessageSquare, color: '#5856D6' },
                  { tool: 'Project/Task Tracking', desc: 'Task Workflow Engine with Kanban, dependencies, risk flags, and workflow reports', integration: 'Tasks link to captures; actions create tasks from AI', icon: Activity, color: '#007AFF' },
                  { tool: 'Document Collaboration', desc: 'Attachment annotations with pins, @mentions, reply threads, and resolve/reopen', integration: 'Review directly on documents, never leave the task', icon: FileText, color: '#AF52DE' },
                  { tool: 'Visual Collaboration', desc: 'Knowledge Maps with network graphs, community detection, and anomaly analysis', integration: 'Auto-extracted from articles and tasks', icon: Brain, color: '#34C759' },
                  { tool: 'Async Standups', desc: 'Daily standup with knowledge-sharing field — insights travel with status updates', integration: 'Replaces sync meetings; knowledge persists', icon: Calendar, color: '#FF9500' },
                ].map((item, i) => {
                  const ItemIcon = item.icon;
                  return (
                    <div key={i} className="p-3 rounded-lg border border-[#E5E7EB]" data-testid={`tech-${i}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: item.color + '12' }}>
                          <ItemIcon className="w-3.5 h-3.5" style={{ color: item.color }} />
                        </div>
                        <h4 className="text-xs font-semibold text-[#0A0A0A]">{item.tool}</h4>
                      </div>
                      <p className="text-[10px] text-[#4B5563] mb-1.5">{item.desc}</p>
                      <p className="text-[9px] text-[#002FA7] flex items-center gap-1"><ChevronRight className="w-2.5 h-2.5" />{item.integration}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== CULTURE PRACTICES ==================== */}
        <TabsContent value="culture" className="space-y-4">
          {/* 3 Cultural Practices */}
          <Card className="border-[#E5E7EB]" data-testid="culture-practices">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users className="w-4 h-4 text-[#5856D6]" />Three Cultural Practices for Knowledge Sharing</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {[
                {
                  title: '1. "Teach What You Learn" Norm',
                  problem: 'Knowledge hoarding',
                  practice: 'Every time someone solves a problem or learns something new, they post it as a capture. Team leads model this by posting at least 3 captures per week. In weekly retrospectives, highlight the most-reused captures. Recognition makes sharing the default.',
                  leadership: 'Leaders post first. Every Monday, the team lead shares one insight from the previous week in the workspace. This signals "sharing is expected, not optional."',
                  color: '#34C759',
                },
                {
                  title: '2. "No Blame" Blocker Transparency',
                  problem: 'Fear of looking incompetent',
                  practice: 'Blockers are celebrated as learning signals, not failures. When someone posts a blocker, the team response is help — not judgment. Use the Blocker capture type daily. Review blockers in standups as "problems to solve together."',
                  leadership: 'Leaders share their own blockers publicly. When a manager posts "I\'m stuck on X, who can help?" it creates psychological safety for everyone to do the same.',
                  color: '#007AFF',
                },
                {
                  title: '3. "Reuse & Credit" Economy',
                  problem: 'Redundant work, no incentive to share',
                  practice: 'When you reuse someone\'s capture, react with the "Reuse" button — giving visible credit. Track reuse rates in the metrics dashboard. In team meetings, call out the top contributors. "Sarah\'s capture on X saved Mike 3 hours this week."',
                  leadership: 'Leaders publicly attribute knowledge sources: "Based on the insight Alex shared last week, we\'re changing our approach to Y."',
                  color: '#FF9500',
                },
              ].map((p, i) => (
                <div key={i} className="p-4 rounded-xl border-l-[4px] border border-[#E5E7EB]" style={{ borderLeftColor: p.color }} data-testid={`practice-${i}`}>
                  <h4 className="text-sm font-semibold text-[#0A0A0A] mb-1">{p.title}</h4>
                  <Badge className="text-[8px] mb-2" style={{ backgroundColor: p.color + '15', color: p.color }}>Solves: {p.problem}</Badge>
                  <p className="text-xs text-[#4B5563] leading-relaxed mb-2">{p.practice}</p>
                  <div className="p-2 rounded-lg bg-[#F9FAFB] border border-[#E5E7EB]">
                    <p className="text-[10px] text-[#0A0A0A]"><span className="font-semibold">Leadership models this:</span> {p.leadership}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Pitfalls & Preventive Measures */}
          <Card className="border-[#E5E7EB]" data-testid="pitfalls-card">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#FF3B30]" />Two Common Pitfalls & Preventive Measures</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {[
                {
                  pitfall: 'Capture Fatigue — Team stops posting after initial enthusiasm (Week 3-4)',
                  why: 'With 12 people, the feed can feel noisy. People stop seeing value and it becomes "another thing to do."',
                  prevention: 'Set a quality bar, not a quantity quota. Encourage 1 meaningful capture per person per day, not 10 shallow ones. Use rooms to segment by project so the feed stays focused. Rotate a weekly "Knowledge Champion" who curates and promotes the best captures.',
                },
                {
                  pitfall: 'Knowledge Silos Despite Tools — Subgroups form and stop cross-sharing',
                  why: 'In a 12-person remote team, 2-3 person clusters naturally emerge. They share within their cluster but not broadly.',
                  prevention: 'Create cross-functional rooms and require at least 1 capture per week in the shared "All Captures" feed. In bi-weekly syncs, randomly pair team members from different clusters for 15-min knowledge exchange calls. Track unique contributors in metrics and flag when someone hasn\'t contributed in 5+ days.',
                },
              ].map((p, i) => (
                <div key={i} className="p-4 rounded-xl border border-[#FF3B30]/20 bg-[#FF3B30]/[0.02]" data-testid={`pitfall-${i}`}>
                  <div className="flex items-start gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-[#FF3B30] flex-shrink-0 mt-0.5" />
                    <h4 className="text-xs font-semibold text-[#FF3B30]">{p.pitfall}</h4>
                  </div>
                  <p className="text-[10px] text-[#4B5563] mb-2 ml-6"><strong>Why it happens:</strong> {p.why}</p>
                  <div className="ml-6 p-2 rounded-lg bg-[#34C759]/5 border border-[#34C759]/20">
                    <p className="text-[10px] text-[#0A0A0A]"><span className="font-semibold text-[#34C759]">Prevention:</span> {p.prevention}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== SUCCESS METRICS ==================== */}
        <TabsContent value="metrics" className="space-y-4">
          {metrics ? (
            <>
              {/* 3 Key Success Indicators */}
              <Card className="border-[#E5E7EB]" data-testid="success-indicators">
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Target className="w-4 h-4 text-[#002FA7]" />30-Day Success Indicators</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { label: 'Knowledge Capture Rate', value: metrics.success_indicators.knowledge_capture_rate, target: 'Target: 60+ captures/month (5+ per person)', icon: Lightbulb, color: metrics.success_indicators.knowledge_capture_rate >= 60 ? '#34C759' : '#FF9500', desc: 'Are people capturing knowledge as they work?' },
                      { label: 'Unique Contributors', value: `${metrics.success_indicators.unique_contributors}/12`, target: 'Target: 10+ of 12 people contributing', icon: Users, color: metrics.success_indicators.unique_contributors >= 10 ? '#34C759' : '#FF9500', desc: 'Is knowledge sharing distributed, not concentrated?' },
                      { label: 'Reuse Rate', value: `${metrics.success_indicators.reuse_rate_pct}%`, target: 'Target: 30%+ captures get reactions', icon: Repeat, color: metrics.success_indicators.reuse_rate_pct >= 30 ? '#34C759' : '#FF9500', desc: 'Is captured knowledge actually being found and reused?' },
                    ].map((s, i) => {
                      const SIcon = s.icon;
                      return (
                        <div key={i} className="p-4 rounded-xl border border-[#E5E7EB]" data-testid={`indicator-${i}`}>
                          <SIcon className="w-5 h-5 mb-2" style={{ color: s.color }} />
                          <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
                          <p className="text-xs font-semibold text-[#0A0A0A] mt-1">{s.label}</p>
                          <p className="text-[10px] text-[#4B5563] mt-0.5">{s.desc}</p>
                          <Badge variant="outline" className="text-[8px] mt-2 border-[#E5E7EB]">{s.target}</Badge>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Detailed Metrics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="border-[#E5E7EB]" data-testid="capture-breakdown">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Layers className="w-4 h-4 text-[#FF9500]" />Captures by Type</CardTitle></CardHeader>
                  <CardContent>
                    {CAPTURE_TYPES.map(t => {
                      const count = metrics.capture_by_type[t.value] || 0;
                      const maxCount = Math.max(...Object.values(metrics.capture_by_type || {}), 1);
                      return (
                        <div key={t.value} className="flex items-center gap-3 py-1.5">
                          <span className="text-[10px] w-16 text-right" style={{ color: t.color }}>{t.label}</span>
                          <div className="flex-1 h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${(count / maxCount) * 100}%`, backgroundColor: t.color }} />
                          </div>
                          <span className="text-[10px] font-bold w-6" style={{ color: t.color }}>{count}</span>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>

                <Card className="border-[#E5E7EB]" data-testid="contributors-list">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#34C759]" />Top Contributors</CardTitle></CardHeader>
                  <CardContent>
                    {metrics.top_contributors.length === 0 ? (
                      <p className="text-xs text-[#9CA3AF] text-center py-4">No contributions yet</p>
                    ) : metrics.top_contributors.map((c, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#F3F4F6] last:border-0" data-testid={`contributor-${i}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold text-[#9CA3AF] w-4">{i + 1}</span>
                          <span className="text-xs font-medium text-[#0A0A0A]">{c.name}</span>
                        </div>
                        <Badge variant="outline" className="text-[9px]">{c.count} captures</Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Total Captures', value: metrics.totals.total_captures, icon: Lightbulb, color: '#FF9500' },
                  { label: 'Promoted to KB', value: metrics.totals.total_promoted, icon: ArrowUpRight, color: '#34C759' },
                  { label: 'Workspace Rooms', value: metrics.totals.total_rooms, icon: Hash, color: '#5856D6' },
                  { label: 'Standups (30d)', value: metrics.totals.total_standups_30d, icon: Calendar, color: '#007AFF' },
                  { label: 'Lessons Learned', value: metrics.totals.total_lessons || 0, icon: GraduationCap, color: '#AF52DE' },
                  { label: 'Open Lessons', value: metrics.totals.open_lessons || 0, icon: AlertTriangle, color: (metrics.totals.open_lessons || 0) > 0 ? '#FF9500' : '#34C759' },
                ].map((s, i) => {
                  const SIcon = s.icon;
                  return (
                    <Card key={i} className="border-[#E5E7EB]" data-testid={`total-${i}`}>
                      <CardContent className="p-3 text-center">
                        <SIcon className="w-4 h-4 mx-auto mb-1" style={{ color: s.color }} />
                        <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
                        <p className="text-[9px] text-[#9CA3AF]">{s.label}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          ) : (
            <Card className="border-[#E5E7EB]"><CardContent className="p-12 text-center text-[#9CA3AF]">Loading metrics...</CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Room Dialog */}
      <Dialog open={isRoomDialogOpen} onOpenChange={setIsRoomDialogOpen}>
        <DialogContent data-testid="create-room-dialog">
          <DialogHeader><DialogTitle>Create Workspace Room</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Room Name *</Label><Input value={roomForm.name} onChange={e => setRoomForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Project Alpha, Onboarding, Research" data-testid="room-name-input" /></div>
            <div><Label>Description</Label><Textarea value={roomForm.description} onChange={e => setRoomForm(p => ({ ...p, description: e.target.value }))} placeholder="What this room is for..." rows={2} data-testid="room-desc-input" /></div>
            <div>
              <Label>Room Type</Label>
              <Select value={roomForm.room_type} onValueChange={v => setRoomForm(p => ({ ...p, room_type: v }))}>
                <SelectTrigger data-testid="room-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="project">Project</SelectItem>
                  <SelectItem value="knowledge-capture">Knowledge Capture</SelectItem>
                  <SelectItem value="standup">Standup</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRoomDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleCreateRoom} data-testid="save-room-btn">Create Room</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Lesson Dialog */}
      <Dialog open={isLessonDialogOpen} onOpenChange={setIsLessonDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="lesson-dialog">
          <DialogHeader><DialogTitle>{editingLesson ? 'Edit Lesson Learned' : 'Capture Lesson Learned'}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div><Label>Title *</Label><Input value={lessonForm.title} onChange={e => setLessonForm(p => ({ ...p, title: e.target.value }))} placeholder="Brief title of the lesson" data-testid="lesson-title-input" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={lessonForm.category} onValueChange={v => setLessonForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="lesson-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="process">Process</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="communication">Communication</SelectItem>
                    <SelectItem value="planning">Planning</SelectItem>
                    <SelectItem value="risk">Risk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Impact</Label>
                <Select value={lessonForm.impact} onValueChange={v => setLessonForm(p => ({ ...p, impact: v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="lesson-impact"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Outcome</Label>
                <Select value={lessonForm.outcome} onValueChange={v => setLessonForm(p => ({ ...p, outcome: v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="lesson-outcome"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="positive">Positive</SelectItem>
                    <SelectItem value="negative">Negative</SelectItem>
                    <SelectItem value="neutral">Neutral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Context</Label><Textarea rows={2} className="text-xs" value={lessonForm.context} onChange={e => setLessonForm(p => ({ ...p, context: e.target.value }))} placeholder="What was the project/situation?" data-testid="lesson-context" /></div>
            <div><Label className="text-xs">What Happened</Label><Textarea rows={2} className="text-xs" value={lessonForm.what_happened} onChange={e => setLessonForm(p => ({ ...p, what_happened: e.target.value }))} placeholder="Describe the event or situation..." data-testid="lesson-what-happened" /></div>
            <div><Label className="text-xs">Root Cause</Label><Textarea rows={2} className="text-xs" value={lessonForm.root_cause} onChange={e => setLessonForm(p => ({ ...p, root_cause: e.target.value }))} placeholder="Why did it happen?" data-testid="lesson-root-cause" /></div>
            <div><Label className="text-xs">Lesson Learned</Label><Textarea rows={2} className="text-xs" value={lessonForm.lesson_learned} onChange={e => setLessonForm(p => ({ ...p, lesson_learned: e.target.value }))} placeholder="What did we learn from this?" data-testid="lesson-learned-input" /></div>
            <div><Label className="text-xs">Recommended Action</Label><Textarea rows={2} className="text-xs" value={lessonForm.recommended_action} onChange={e => setLessonForm(p => ({ ...p, recommended_action: e.target.value }))} placeholder="What should we do differently next time?" data-testid="lesson-action" /></div>
            <div><Label className="text-xs">Tags (comma-separated)</Label><Input className="text-xs" value={lessonForm.tags} onChange={e => setLessonForm(p => ({ ...p, tags: e.target.value }))} placeholder="deployment, api, process" data-testid="lesson-tags" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLessonDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleCreateLesson} data-testid="save-lesson-btn">
              {editingLesson ? 'Update Lesson' : 'Capture Lesson'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default WorkspacePage;
